import React, { useEffect, useState } from 'react';
import style from './style.module.scss';
import DropDownHeader from 'design-v2/common/DropDownHeader';
import { ListOption } from 'design-v2/common/RadioList';
import AlertPanel from 'external-components/alert-panel';
import { IBookingStatus, IPaymentDetailsBase, BookingType } from 'shared-types/index';
import ModalContainer from 'design-v2/common/ModalContainer';
import { IValidationErrorListItem } from 'external-services/booking-validation-service/bookingValidation.types';
import StatusSymbol, { StatusTitleRender } from 'design-v2/common/StatusSymbol';
import CircularProgress from '@material-ui/core/CircularProgress';

const NS = 'StatusSelectorModal';

export default function StatusSelectorModal({
  statusId,
  bookingStatusList,
  closeDialog,
  handleStatusChange,
  StaffPanel = null,
  staffRequired = false,
  canSave = true,
  cantSaveMessageList = [],
  handleQuickSelectStatus,
  paymentDetails,
  bookingType,
  staffEditEnabled,
  isDiaryBookingCreator
}: {
  statusId: string;
  bookingStatusList: IBookingStatus[];
  closeDialog: () => void;
  handleStatusChange: (status: string) => Promise<void>;
  StaffPanel?: () => JSX.Element;
  handleQuickSelectStatus?: (status: IBookingStatus) => void;
  staffRequired?: boolean;
  canSave?: boolean;
  cantSaveMessageList: IValidationErrorListItem[];
  paymentDetails: IPaymentDetailsBase;
  bookingType: BookingType;
  staffEditEnabled: boolean;
  isDiaryBookingCreator?: boolean;
}) {
  const [selectedStatusId, setSelectedStatusId] = useState<string>(statusId);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    const foundStatus = bookingStatusList.find((status) => status._id === selectedStatusId);
    handleQuickSelectStatus(foundStatus);
  }, [selectedStatusId]);

  const dropDownItems: {
    title: string | JSX.Element;
    children: JSX.Element;
    colour?: string;
    tailTitle: any;
    numberValue: any;
    disabled: boolean;
    id: string;
  }[] = bookingStatusList.map((option: IBookingStatus, index: number) => {
    return {
      title: (
        <StatusTitleRender
          status={option}
          paymentDetails={paymentDetails}
          bookingType={bookingType}
        />
      ),
      children: (
        <StatusSymbol
          status={option}
          bookingType={bookingType}
          paymentDetails={paymentDetails}
          isModal={true}
        />
      ),
      id: option._id,
      tailTitle: null as any,
      numberValue: null as any,
      disabled: false,
    };
  });

  const handleSelectStatus = (id: string) => {
    if (staffEditEnabled) {
      setSelectedStatusId(id);
    } else {
      handleStatusChangeWithLoading(id);
    }
  };

  const handleStatusChangeWithLoading = async (id: string) => {
    setIsLoading(true);
    // Small delay before showing loader to prevent flicker for fast operations
    const loaderTimeout = setTimeout(() => {
      setShowLoader(true);
    }, 300);
    
    try {
      await handleStatusChange(id);
    } finally {
      clearTimeout(loaderTimeout);
      setIsLoading(false);
      setShowLoader(false);
    }
  };

  const handleSaveWithLoading = async () => {
    await handleStatusChangeWithLoading(selectedStatusId);
  };

  return (
    <div>
      {statusId && (
        <ModalContainer
          closeTimeoutMS={700}
          isOpen={Boolean(statusId)}
          onRequestClose={closeDialog}
          isMaxWidth={false}
          isDiaryBookingCreator={isDiaryBookingCreator}
          contentStyle={{ maxWidth: '500px', width: '80%', inset: '50% 50% auto 50%' }}
        >
          <div className={style.modalContent}>
            {isLoading && (
              <div className={`${style.loadingOverlay} ${showLoader ? style.fadeIn : ''}`}>
                <CircularProgress size={50} />
              </div>
            )}
            {staffEditEnabled ? (
              <DropDownHeader
                isColorOverride={!canSave}
                buttonDisable={!canSave || isLoading}
                title={'Booking'}
                handleCancel={closeDialog}
                handleSave={handleSaveWithLoading}
              />
            ) : null}
            {!canSave && (
              <div className={style.alertWrap}>
                <AlertPanel
                  severity="warning"
                  message={`There are problems with your booking that need addressing before you can save:`}
                  messageList={cantSaveMessageList}
                />
              </div>
            )}
            {dropDownItems.map((item, i) => {
              const checked = selectedStatusId === item.id
              return (
                <ListOption
                  dataTestId={`dropdown-option-${i}`}
                  key={i}
                  title={item.title}
                  icon={item.children}
                  index={i}
                  onClick={() => {
                    if (!item.disabled && !isLoading) {
                      handleSelectStatus(item.id);
                    }
                  }}
                  checked={selectedStatusId === item.id}
                  hiddenBorder={i === dropDownItems.length - 1}
                  hiddenRadio={true}
                  disabled={item.disabled || isLoading}
                  style={{
                    background: checked ? '#ffeff9' : 'unset'
                  }}
                />
              );
            })}
            {staffRequired && StaffPanel()}
          </div>
        </ModalContainer>
      )}
    </div>
  );
}
